import { HTMLAttributes, forwardRef } from 'react';
import { cn } from '../utils/cn';

export interface LoadingSpinnerProps extends HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'overlay';
  label?: string;
}

/**
 * LoadingSpinner - Accessible loading indicator component
 * 
 * Required CSS classes:
 * - .spinner - Base spinner styles
 * - .spinner-sm, .spinner-md, .spinner-lg - Size variants
 * - .spinner-overlay - Overlay variant (full-screen overlay)
 * - .spinner-label - Label text styles
 * 
 * Accessibility:
 * - Screen reader: Announces loading state via aria-label
 * - Focus: Overlay variant prevents interaction
 * 
 * @example
 * <LoadingSpinner size="lg" label="Loading data..." />
 * 
 * @example
 * <LoadingSpinner variant="overlay" label="Processing..." />
 */
export const LoadingSpinner = forwardRef<HTMLDivElement, LoadingSpinnerProps>(
  ({ className, size = 'md', variant = 'default', label = 'Loading...', ...props }, ref) => {
    const spinner = (
      <div
        ref={ref}
        className={cn(
          'spinner',
          `spinner-${size}`,
          variant === 'overlay' && 'spinner-overlay',
          className
        )}
        role="status"
        aria-label={label}
        aria-live="polite"
        {...props}
      >
        <span className="spinner-circle" aria-hidden="true" />
        {label && (
          <span className="spinner-label" aria-hidden="true">
            {label}
          </span>
        )}
      </div>
    );

    return spinner;
  }
);

LoadingSpinner.displayName = 'LoadingSpinner';
